import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPinIcon,
  BuildingOfficeIcon,
  StarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

interface Destination {
  _id: string;
  name: string;
  image: string;
  description: string;
  resortCount?: number;
  averageRating?: number;
  popularActivities?: string[];
  climate?: string;
  bestTimeToVisit?: string;
  highlights?: string[];
  isActive: boolean;
  sortOrder?: number;
}

const DestinationsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper function to resolve image URLs
  const resolveImageUrl = (imagePath: string | undefined): string => {
    if (!imagePath) return 'https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
    const normalized = String(imagePath).replace(/\\/g, '/');
    
    // If Cloudinary URL, return as-is
    if (normalized.includes('cloudinary.com')) {
      return normalized;
    }
    
    // If absolute URL or data URL, return as-is
    if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
      return normalized;
    }
    
    if (normalized.startsWith('/uploads')) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      return `${baseUrl}${normalized}`;
    }
    return imagePath;
  };

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/destinations?active=true`);
      
      if (response.data && response.data.destinations) {
        // Transform destinations to include default values for missing fields
        const transformedDestinations = response.data.destinations.map((dest: any) => ({
          ...dest,
          resortCount: dest.resortCount || 0,
          averageRating: dest.averageRating || 4.5,
          popularActivities: dest.popularActivities || ['Sightseeing', 'Beach Activities', 'Cultural Tours'],
          climate: dest.climate || 'Tropical',
          bestTimeToVisit: dest.bestTimeToVisit || 'April - October',
          highlights: dest.highlights || [dest.description?.slice(0, 100) + '...' || 'Beautiful destination in Bali']
        }));
        setDestinations(transformedDestinations);
      }
    } catch (err) {
      console.error('Error fetching destinations:', err);
      setError('Failed to load destinations. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredDestinations = destinations
    .filter(destination =>
      destination.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      destination.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (destination.popularActivities && destination.popularActivities.some(activity => 
        activity.toLowerCase().includes(searchQuery.toLowerCase())
      ))
    )
    .sort((a, b) => {
      // Sort by resort count (descending - more resorts first)
      const countA = a.resortCount || 0;
      const countB = b.resortCount || 0;
      return countB - countA;
    });

  return (
    <>
      <Helmet>
        <title>Destinations - Discover Beautiful Bali | Bali Getaway</title>
        <meta name="description" content="Explore the most beautiful destinations in Bali. From vibrant Seminyak to cultural Ubud, find your perfect tropical paradise." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-br from-yellow-500 to-orange-600">
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" 
              alt="Bali landscape" 
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <div className="flex items-center mb-4">
                <img src="/logo.png" alt="Bali Getaway" className="h-12 w-auto mr-4" />
                <span className="text-2xl font-bold">Destinations</span>
              </div>
              <h1 className="text-5xl font-bold mb-4">
                Discover Bali's Most Beautiful Destinations
              </h1>
              <p className="text-xl mb-8">
                From pristine beaches to cultural treasures, explore the diverse landscapes and experiences that make Bali magical.
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-md">
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-3 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                />
                <MapPinIcon className="absolute right-4 top-3 h-6 w-6 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Destinations Grid */}
        <div className="container mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full shadow-2xl mb-8 animate-bounce">
              <MapPinIcon className="h-6 w-6 mr-3" />
              <span className="text-lg font-black uppercase tracking-widest">🏝️ DISCOVER BALI 🏝️</span>
            </div>
            
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6">
              {loading ? (
                <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent animate-pulse">
                  Loading Magic...
                </span>
              ) : searchQuery ? (
                <>Found <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">{filteredDestinations.length}</span> Results</>
              ) : (
                <>Explore <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">{destinations.length}</span> Paradise Destinations</>
              )}
            </h2>
            
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
              From pristine beaches to mystical temples, each destination offers a unique slice of Balinese paradise. 
              Choose your perfect tropical escape and create memories that will last a lifetime.
            </p>
            
            {/* Decorative Elements */}
            <div className="flex items-center justify-center space-x-6 text-gray-400">
              <div className="h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent flex-1 max-w-32"></div>
              <span className="text-2xl">🌺</span>
              <div className="h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent flex-1 max-w-32"></div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading destinations...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <MapPinIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">Error Loading Destinations</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button 
                onClick={fetchDestinations}
                className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredDestinations.length === 0 ? (
            <div className="text-center py-16">
              <MapPinIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {searchQuery ? 'No destinations found' : 'No destinations available'}
              </h3>
              <p className="text-gray-500">
                {searchQuery ? 'Try adjusting your search terms to find what you\'re looking for.' : 'Please check back later for available destinations.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredDestinations.map((destination, index) => (
                <div key={destination._id} className="group relative overflow-hidden bg-white rounded-3xl shadow-2xl hover:shadow-orange-500/25 transition-all duration-700 transform hover:-translate-y-3 hover:scale-105">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 opacity-50"></div>
                  
                  {/* Main Image */}
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={resolveImageUrl(destination.image)}
                      alt={destination.name}
                      className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-1000 ease-out"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                      }}
                    />
                    
                    {/* Gradient Overlays */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-600/30 via-transparent to-yellow-600/30 group-hover:from-orange-600/50 group-hover:to-yellow-600/50 transition-all duration-700"></div>
                    
                    {/* Rating Badge */}
                    <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-xl">
                      <div className="flex items-center space-x-2">
                        <StarIcon className="h-5 w-5 text-yellow-500 fill-current" />
                        <span className="font-bold text-gray-900">{destination.averageRating}</span>
                      </div>
                    </div>
                    
                    {/* Package Count Badge */}
                    {(destination.resortCount || 0) > 0 && (
                      <div className="absolute top-6 left-6 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-2xl px-4 py-2 shadow-xl">
                        <div className="flex items-center space-x-2">
                          <BuildingOfficeIcon className="h-4 w-4" />
                          <span className="font-bold text-sm">{destination.resortCount || 0} Resorts</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Destination Name Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <h3 className="text-4xl font-black text-white mb-3 drop-shadow-2xl group-hover:text-yellow-300 transition-colors duration-300">
                        {destination.name}
                      </h3>
                      
                      {/* Quick Info */}
                      <div className="flex items-center space-x-4 text-white/90 mb-4">
                        <span className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-medium">
                          🌴 {destination.climate}
                        </span>
                        <span className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-medium">
                          📅 {destination.bestTimeToVisit}
                        </span>
                      </div>
                    </div>
                    
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-700"></div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="relative p-8">
                    {/* Description */}
                    <p className="text-gray-700 mb-6 leading-relaxed line-clamp-3">
                      {destination.description}
                    </p>
                    
                    {/* Activities Tags */}
                    {destination.popularActivities && destination.popularActivities.length > 0 && (
                      <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                          {destination.popularActivities.slice(0, 3).map((activity, actIndex) => (
                            <span key={actIndex} className="bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold border border-orange-200">
                              {activity}
                            </span>
                          ))}
                          {destination.popularActivities.length > 3 && (
                            <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-semibold">
                              +{destination.popularActivities.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* CTA Button */}
                    <Link
                      to={`/destinations/${destination._id}`}
                      className="group/btn relative block w-full overflow-hidden bg-gradient-to-r from-orange-500 via-yellow-500 to-red-500 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-yellow-600 to-red-600 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center justify-center space-x-3">
                        <span className="text-lg">Explore {destination.name}</span>
                        <ArrowRightIcon className="h-6 w-6 group-hover/btn:translate-x-2 transition-transform duration-300" />
                      </div>
                      
                      {/* Button Shine Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover/btn:opacity-100 group-hover/btn:animate-pulse transition-opacity duration-500"></div>
                    </Link>
                  </div>
                  
                  {/* Card Border Glow */}
                  <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-orange-400 group-hover:to-yellow-400 transition-all duration-500"></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Why Choose Bali Section */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Bali?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Bali offers an incredible diversity of experiences, from spiritual temples to world-class beaches, making it the perfect destination for any traveler.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <MapPinIcon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Diverse Destinations</h3>
                <p className="text-gray-600">
                  From bustling beach towns to serene mountain retreats, Bali offers something for every type of traveler.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-secondary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <BuildingOfficeIcon className="h-8 w-8 text-secondary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Luxury Accommodations</h3>
                <p className="text-gray-600">
                  Stay in world-class resorts and boutique hotels that offer unparalleled comfort and authentic Balinese hospitality.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-secondary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <StarIcon className="h-8 w-8 text-secondary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Unforgettable Experiences</h3>
                <p className="text-gray-600">
                  Create lasting memories with unique cultural experiences, adventure activities, and wellness treatments.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-primary-600 to-secondary-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Bali Adventure?
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Browse our curated packages and discover the perfect combination of destinations for your dream vacation.
            </p>
            <Link
              to="/packages"
              className="inline-flex items-center bg-white text-secondary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors"
            >
              View Packages
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default DestinationsPage;
